import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useFeedSocket } from '@/contexts/FeedSocketContext'
import { feedService } from '@/services/feedService'
import {
  FeedSocketEvent,
  PostVisibility
} from '@/types/feed'
import type {
  IFeedPostCreatedPayload,
  IFeedPostDeletedPayload,
  IPost
} from '@/types/feed'
import PostCard from './PostCard'

const PAGE_SIZE = 20

interface FeedListProps {
  visibility?: PostVisibility
  /** Optional ref to a method that lets parents prepend a freshly-created post. */
  registerPrepend?: (fn: (post: IPost) => void) => void
}

const FeedList: React.FC<FeedListProps> = ({ visibility, registerPrepend }) => {
  const { on } = useFeedSocket()
  const [posts, setPosts] = useState<IPost[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const loadPage = useCallback(async (nextCursor?: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const page = await feedService.getFeed({
        limit: PAGE_SIZE,
        ...(nextCursor && { cursor: nextCursor }),
        ...(visibility && { visibility })
      })
      setPosts((prev) => {
        if (!nextCursor) return page.items
        const seen = new Set(prev.map((p) => p.id))
        return [...prev, ...page.items.filter((p) => !seen.has(p.id))]
      })
      setHasMore(page.hasMore)
      setCursor(page.nextCursor)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load feed')
    } finally {
      setLoading(false)
    }
  }, [visibility])

  // Initial load + reload on visibility change.
  useEffect(() => {
    setPosts([])
    setCursor(undefined)
    void loadPage(undefined)
  }, [loadPage])

  // Realtime additions.
  useEffect(() => {
    const offCreated = on<IFeedPostCreatedPayload>(FeedSocketEvent.POST_CREATED, (p) => {
      setPosts((prev) => (prev.find((x) => x.id === p.post.id) ? prev : [p.post, ...prev]))
    })
    const offDeleted = on<IFeedPostDeletedPayload>(FeedSocketEvent.POST_DELETED, (p) => {
      setPosts((prev) => prev.filter((x) => x.id !== p.postId))
    })
    return () => {
      offCreated()
      offDeleted()
    }
  }, [on])

  // Allow the parent (PostComposer host) to prepend optimistically.
  useEffect(() => {
    if (!registerPrepend) return
    registerPrepend((post) => {
      setPosts((prev) => (prev.find((x) => x.id === post.id) ? prev : [post, ...prev]))
    })
  }, [registerPrepend])

  // IntersectionObserver-driven infinite scroll.
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return
    const node = sentinelRef.current
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry?.isIntersecting && !loading) {
        void loadPage(cursor)
      }
    }, { rootMargin: '300px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, cursor, loadPage, loading])

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          onDeleted={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
        />
      ))}
      {!loading && posts.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          Nothing in your feed yet. Share the first update above.
        </div>
      )}
      {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-1" />}
      {loading && (
        <div className="flex items-center justify-center py-4 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </div>
  )
}

export default FeedList
